import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Network, Cable, Shield, Video, Package, VideoIcon, ArrowRight, CheckCircle, Sparkles, Target, Clock, Award, Users, TrendingUp, Zap, X, Star } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { Button } from '../components/ui/button';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({});

  const scrollToServices = () => {
    document.getElementById('services-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroSlides = [
    {
      title: "Comprehensive IT Solutions",
      subtitle: "From network infrastructure to cutting-edge audio-visual systems",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070",
      highlight: "Professional Services"
    },
    {
      title: "Enterprise-Grade Infrastructure",
      subtitle: "Scalable solutions designed for your business growth",
      image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069",
      highlight: "Trusted Expertise"
    },
    {
      title: "24/7 Support & Maintenance",
      subtitle: "Keeping your business connected and operational",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070",
      highlight: "Always Available"
    }
  ];

  const stats = [
    { icon: Users, value: 500, suffix: '+', label: 'Projects Completed', description: 'Successful implementations' },
    { icon: Award, value: 98, suffix: '%', label: 'Client Satisfaction', description: 'Happy customers' },
    { icon: Clock, value: 24, suffix: '/7', label: 'Support Available', description: 'Round-the-clock service' },
    { icon: TrendingUp, value: 15, suffix: '+', label: 'Years Experience', description: 'Industry expertise' },
  ];

  const services = [
    {
      icon: Network,
      title: 'Data Center Migration',
      slug: 'broadcom-migration',
      description: 'End-to-end data center migration: planning, compatibility analysis, staged rollout, configuration & policy mapping, testing and post-migration optimization.',
      accent: 'from-blue-600 to-cyan-500',
      features: ['Migration Planning', 'Compatibility Analysis', 'Staged Rollout', 'Policy Mapping', 'Testing & Validation', 'Post-Migration Support'],
      timeline: '4-8 weeks',
      complexity: 'Enterprise',
      deliverables: ['Migration Blueprint', 'Compatibility Report', 'Configuration Documents', 'Training Materials']
    },
    {
      icon: Cable,
      title: 'Office Recabling',
      slug: 'office-recabling',
      description: 'Structured cabling for offices: Cat6/Cat6a/Cat7 design and install, cable management, labeling, testing and certification for reliable connectivity.',
      accent: 'from-green-600 to-emerald-500',
      features: ['Network Design', 'Cable Installation', 'Cable Management', 'Professional Labeling', 'Testing & Certification', 'Documentation'],
      timeline: '2-6 weeks',
      complexity: 'Medium',
      deliverables: ['Network Diagram', 'Cable Schedule', 'Test Reports', 'As-Built Documentation']
    },
    {
      icon: Shield,
      title: 'Switch & Firewall',
      slug: 'switch-firewall',
      description: 'Supply, install and configure network switches and enterprise firewalls — VLANs, ACLs, HA, VPNs, segmentation and security hardening.',
      accent: 'from-indigo-600 to-violet-500',
      features: ['Device Procurement', 'VLAN Configuration', 'Firewall Rules', 'HA Setup', 'VPN Configuration', 'Security Hardening'],
      timeline: '3-5 weeks',
      complexity: 'High',
      deliverables: ['Network Architecture', 'Security Policy', 'Configuration Backup', 'Runbook']
    },
    {
      icon: Video,
      title: 'Audio & Video Solutions',
      slug: 'audio-video-solutions',
      description: 'AV installs: meeting rooms, boardrooms and auditoriums. Speakers, mixers, displays, amplifiers, calibration and control integration.',
      accent: 'from-yellow-500 to-orange-400',
      features: ['Room Assessment', 'Equipment Supply', 'Professional Installation', 'Audio Calibration', 'Control Integration', 'User Training'],
      timeline: '1-4 weeks',
      complexity: 'Medium',
      deliverables: ['Room Design', 'Equipment List', 'Calibration Report', 'User Guides']
    },
    {
      icon: Package,
      title: 'IT Equipment Warehousing',
      slug: 'it-equipment-warehousing',
      description: 'Secure warehousing and inventory management for IT equipment — receiving, asset tagging, climate control and dispatch logistics.',
      accent: 'from-slate-600 to-gray-500',
      features: ['Secure Storage', 'Asset Tagging', 'Inventory Management', 'Climate Control', 'Dispatch Logistics', 'Real-time Tracking'],
      timeline: 'Ongoing',
      complexity: 'Low',
      deliverables: ['Inventory Dashboard', 'Asset Reports', 'Dispatch Logs', 'Audit Trail']
    },
    {
      icon: VideoIcon,
      title: 'Conferencing Solutions',
      slug: 'conferencing-solutions',
      description: 'Audio & video conferencing replacements and installs: Zoom/Teams room setups, cameras, mics, codec integration and room scheduling systems.',
      accent: 'from-pink-600 to-rose-500',
      features: ['Platform Integration', 'Hardware Setup', 'Camera Installation', 'Audio Configuration', 'Room Scheduling', 'Remote Management'],
      timeline: '1-3 weeks',
      complexity: 'Medium',
      deliverables: ['Integration Guide', 'Hardware Specs', 'Training Sessions', 'Support Documentation']
    },
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Tailored Solutions',
      description: 'Custom-designed services that align perfectly with your business objectives and technical requirements.'
    },
    {
      icon: Award,
      title: 'Certified Experts',
      description: 'Team of industry-certified professionals with extensive experience in enterprise IT infrastructure.'
    },
    {
      icon: Zap,
      title: 'Rapid Deployment',
      description: 'Efficient project management ensuring minimal downtime and quick turnaround times.'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Rigorous testing and validation processes to ensure optimal performance and reliability.'
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation',
      description: 'We assess your needs, infrastructure, and business goals to create a tailored solution.',
      icon: Users
    },
    {
      step: '02',
      title: 'Planning',
      description: 'Detailed project planning with timelines, milestones, and resource allocation.',
      icon: Target
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Professional installation and configuration by our certified technical team.',
      icon: Zap
    },
    {
      step: '04',
      title: 'Support',
      description: 'Ongoing maintenance, monitoring, and 24/7 technical support for peace of mind.',
      icon: Shield
    },
  ];

  const industries = [
    { name: 'Healthcare', projects: '120+', icon: '🏥' },
    { name: 'Finance', projects: '85+', icon: '💼' },
    { name: 'Education', projects: '150+', icon: '🎓' },
    { name: 'Retail', projects: '95+', icon: '🛍️' },
    { name: 'Manufacturing', projects: '70+', icon: '🏭' },
    { name: 'Technology', projects: '200+', icon: '💻' },
  ];

  const testimonials = [
    {
      name: 'John Martinez',
      role: 'IT Director',
      company: 'TechCorp Solutions',
      image: 'https://randomuser.me/api/portraits/men/1.jpg',
      content: 'Tasks IT Services transformed our entire network infrastructure. The data center migration was seamless, and their team was professional throughout.',
      rating: 5,
      service: 'Data Center Migration'
    },
    {
      name: 'Sarah Williams',
      role: 'Operations Manager',
      company: 'Global Enterprises',
      image: 'https://randomuser.me/api/portraits/women/2.jpg',
      content: 'Outstanding office recabling service. They completed the project ahead of schedule with minimal disruption to our operations.',
      rating: 5,
      service: 'Office Recabling'
    },
    {
      name: 'David Chen',
      role: 'CTO',
      company: 'Innovation Labs',
      image: 'https://randomuser.me/api/portraits/men/3.jpg',
      content: 'Their conferencing solutions have revolutionized how we collaborate. The installation was flawless and the training was comprehensive.',
      rating: 5,
      service: 'Conferencing Solutions'
    },
  ];

  // Animate stats on mount
  useEffect(() => {
    stats.forEach((stat, index) => {
      setTimeout(() => {
        let current = 0;
        const increment = stat.value / 50;
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setAnimatedStats(prev => ({
            ...prev,
            [index]: current.toFixed(0)
          }));
        }, 30);
      }, index * 200);
    });
  }, []);

  const ServiceModal = ({ service, onClose }) => (
    <AnimatePresence>
      {service && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl max-w-3xl w-full p-8 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full">
              <X className="h-6 w-6" />
            </button>
            
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${service.accent} mb-6`}>
              <service.icon className="h-8 w-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold mb-4">{service.title}</h2>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">{service.description}</p>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl text-center">
                <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-semibold text-gray-600">Timeline</div>
                <div className="text-lg font-bold text-blue-600">{service.timeline}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl text-center">
                <Target className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <div className="text-sm font-semibold text-gray-600">Complexity</div>
                <div className="text-lg font-bold text-purple-600">{service.complexity}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl text-center">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <div className="text-sm font-semibold text-gray-600">Success</div>
                <div className="text-lg font-bold text-green-600">98%+</div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-bold mb-3 text-lg">Key Features</h3>
              <div className="grid grid-cols-2 gap-3">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-bold mb-3 text-lg">Deliverables</h3>
              <div className="flex flex-wrap gap-2">
                {service.deliverables.map((item, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            
            <Link to={`/services/${service.slug}`}>
              <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500">
                View Full Details
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <Helmet>
        <title>Services - Tasks IT Services | Professional IT Solutions</title>
        <meta name="description" content="Explore our comprehensive IT services including data center migration, network installation, AV solutions, and more." />
      </Helmet>

      {/* Enhanced Hero Section with Carousel */}
      <section className="relative h-screen overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          className="h-full"
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full">
                <div className="absolute inset-0">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-indigo-900/90 to-purple-800/85"></div>
                  <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="max-w-4xl text-white text-center mx-auto"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20"
                    >
                      <Sparkles className="h-4 w-4 text-yellow-300" />
                      <span className="text-sm font-medium">{slide.highlight}</span>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
                    >
                      {slide.title}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-2xl text-blue-100 mb-8 leading-relaxed"
                    >
                      {slide.subtitle}
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex flex-wrap justify-center gap-4"
                    >
                      <Link to="/contact">
                        <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-lg px-8 py-6 group">
                          Get Started
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Button
                        size="lg"
                        onClick={scrollToServices}
                        className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-xl text-lg px-8 py-6"
                      >
                        View Services
                      </Button>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Animated Stats Section */}
      

      {/* Services Grid */}
      <section id="services-grid" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              Our <span className="text-blue-600">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive IT solutions designed to optimize your business infrastructure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedService(service)}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform opacity-0 group-hover:opacity-100"></div>
                
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`relative inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${service.accent} mb-4 shadow-lg`}
                >
                  <service.icon className="h-7 w-7 text-white" />
                </motion.div>
                
                <h3 className="relative text-xl font-bold mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                <p className="relative text-gray-600 mb-4 text-sm line-clamp-3">{service.description}</p>
                
                <div className="relative flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
                      {service.timeline}
                    </span>
                    <span className="text-xs px-3 py-1 bg-purple-50 text-purple-700 rounded-full">
                      {service.complexity}
                    </span>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-600 group-hover:translate-x-2 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />

      
    
   

      

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Target className="h-20 w-20 mx-auto mb-6 text-blue-200" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Transform Your IT Infrastructure?
            </h2>
            <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's discuss your project requirements and create a tailored solution that drives your business forward.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-xl px-10 py-7 group">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-2xl text-xl px-10 py-7">
                  Learn More
                </Button>
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-12 flex flex-wrap justify-center gap-8 text-blue-100"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Free consultation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Custom solutions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>24/7 support</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;